// ── Recording, without a library ──────────────────────────────────────────────
//
// MediaRecorder is native and does the work. What is not native is knowing
// WHICH container to ask it for, and that is the whole of this file: no browser
// supports every one, no two agree on a default, and asking for one that is not
// supported throws where the person is standing in front of a microphone.
//
// The order below is by what the platform stores well and what the widest set
// of browsers can play back afterwards — opus in webm first (Chrome, Firefox,
// Android), mp4/aac for Safari and iOS. Every entry's container is on
// platform/files' allow-list; adding one here without adding it there produces
// a recording that uploads and is refused.

export const RECORDING_TYPES = [
  { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
  { mimeType: 'audio/mp4', extension: 'm4a' },
  { mimeType: 'audio/mpeg', extension: 'mp3' },
]

const EXTENSIONS = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
}

/** The type without its parameters, lower-cased. Mirrors the backend's rule. */
export function baseType(mimeType) {
  return String(mimeType ?? '').split(';')[0].trim().toLowerCase()
}

/** What to call the file, so a download lands with an extension that opens. */
export function extensionFor(mimeType) {
  return EXTENSIONS[baseType(mimeType)] ?? 'bin'
}

/**
 * The first container this browser will actually record.
 *
 * @param {(type: string) => boolean} [isTypeSupported] normally
 *   `MediaRecorder.isTypeSupported`. Injected rather than reached for so this
 *   is testable off a browser — and because Safari shipped MediaRecorder before
 *   it shipped this method, which is the case `null` covers: construct the
 *   recorder with no options and read `recorder.mimeType` back afterwards.
 */
export function pickRecordingType(isTypeSupported) {
  if (typeof isTypeSupported !== 'function') return null
  return RECORDING_TYPES.find(entry => {
    try { return isTypeSupported(entry.mimeType) } catch { return false }
  }) ?? null
}

/**
 * Seconds as a clock. `0:07`, `1:05`, `12:03`.
 *
 * Anything not a finite number reads as `0:00` rather than `NaN:NaN` — which is
 * not defensiveness but the ordinary case: a webm produced by MediaRecorder has
 * no duration in its header, so an <audio> element reports Infinity for it
 * until it has been seeked to the end. See UiAudio.
 */
export function formatClock(seconds) {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
